import React from 'react'
import 'url-search-params-polyfill'
import { Backdrop, CircularProgress } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { RootState } from '../Redux'
import { useTranslation } from 'react-i18next'
import { Header } from '../Components/Header'
import { Helmet } from 'react-helmet-async'
import {useHistory, useSettings, useSettingsByToken} from '../Utils/hooks'
import { getLocale } from '../Utils/helpers'
import { themeAssets } from '../Utils/themes'
import { get } from 'lodash'
import { ReminderSettingsForm } from '../Components/ReminderSettings/ReminderSettingsForm'
import { AccordionItem } from '../Components/ServiceCardContainer/AccordionItem'
import {ReminderSettings, ThemeType} from '../interfaces'

interface StyleOptions extends ThemeType {
    isEmbedded: boolean,
}

const useStyles = makeStyles<StyleOptions>()((theme: Theme, options: StyleOptions) => ({
    backdrop: {
        zIndex: 1000,
    },
    root: {
        backgroundColor: options.isEmbedded ? options.backgroundColor ?? 'white' : undefined,
        height: '100%',
        fontSize: 24,
        justifyContent: 'center',
        display: 'flex',
    },
    container: {
        margin: theme.spacing(3),
        marginTop: theme.spacing(10),
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 800,
    },
    thankYou: {
        fontWeight: 'bold',
    },
}))

interface ReminderSettingsContainerProps {
    themeName: string
    locale?: string
    embedded: boolean
    setGtmIds: (chainGtmId: string) => void
}

export interface ReminderSettingsSubmitEvent extends ReminderSettings {
}

export const ReminderSettingsContainer = ({ ...props }: ReminderSettingsContainerProps) => {
    const location = useLocation()
    const params = useParams()
    const history = useHistory()
    const language = getLocale()
    const { bookingEnabled, title, isEmbedded, logo, wwwAddress, uiTheme, uiThemeName } = useSelector((state: RootState) => ({
        bookingEnabled: state.ui.settings?.booking?.enabled,
        title: state.ui.settings?.station?.name || state.ui.settings?.chain?.name || '',
        isEmbedded: state.ui.options?.embedded || props.embedded,
        logo: state.ui.settings?.station?.logo?.data || state.ui.settings?.chain?.logo?.data || undefined,
        wwwAddress: state.ui.settings?.station?.www || state.ui.settings?.chain?.www || undefined,
        uiTheme: state.ui.options?.theme,
        uiThemeName: state.ui.options?.themeName,
    }))
    const themeOptions: StyleOptions = {isEmbedded, ...get(themeAssets, uiThemeName || 'default'), ...(uiTheme ?? {})}
    const { classes } = useStyles(themeOptions)
    const query = new URLSearchParams(location.search)
    const token = query.get('token')
    const { t } = useTranslation()
    const chainIdUrl = (params as any).chainId
    const stationIdUrl = (params as any).stationId

    if (! chainIdUrl && ! stationIdUrl && token) {
        useSettingsByToken(token, history, props.embedded)
    } else {
        useSettings(chainIdUrl, stationIdUrl, history, props.embedded)
    }

    const getTitle = () => {
        return `${title} – ${t('reminderSettings.title')}`
    }

    if (bookingEnabled === undefined) {
        return (
            <Backdrop className={classes.backdrop} open={true} style={{ backgroundColor: 'white' }}>
                <CircularProgress size={48} color="primary" />
            </Backdrop>
        )
    }


    const prefillBase64 = null;

    const prefill = prefillBase64 ? JSON.parse(Buffer.from(prefillBase64, 'base64').toString('utf8')) : null

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <Helmet>
                    <html lang={language} />
                    <title>{getTitle()}</title>
                </Helmet>
                <Header
                    title={title}
                    display={!isEmbedded}
                    logo={logo}
                    wwwAddress={wwwAddress}
                />
                <AccordionItem
                    autoHeight
                    heading={t('reminderSettings.title')}
                    content={
                        <ReminderSettingsForm
                            fetching={false}
                            prefill={prefill}
                        />
                    }
                />
            </div>
        </div>
    )
}
